import React, { type FC, useCallback, useContext, useState } from 'react';

import { Button, Modal } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { GrammarPageEntitiesSettings } from 'client/blocks/grammar/GrammarPageEntitiesSettings/GrammarPageEntitiesSettings';
import { GranetCodeEditor } from 'client/blocks/grammar/GranetCodeEditor/GranetCodeEditor';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { SettingsPageContext, useSettingsPageContext } from 'client/context/settingsPageContext';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { Skill } from 'client/model/skill';
import { preventEsc } from 'client/utils/events';
import * as commonKeyset from 'i18n/Common';
import * as keyset from 'i18n/IntentsPage';

import styles from './GrammarPageEntities.module.scss';

const t = i18n(keyset);
const tCommon = i18n(commonKeyset);

interface GrammarPageEntitiesProps {
    aliceApp: Skill;
    disabled: boolean;
}

interface EntitiesSettingsPopupProps {
    onClose: () => void;
    disabled: boolean;
}

const EntitiesSettingsPopup: FC<EntitiesSettingsPopupProps> = ({ onClose, disabled }) => {
    const [isChanged, setIsChanged] = useState(false);
    const [confirmIsOpen, setConfirmIsOpen] = useState(false);

    const currentAliceAppContext = useContext(CurrentAliceAppContext)!;
    const snapshotContext = useContext(SnapshotContext);
    const settingsPageContext = useSettingsPageContext();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onModalClose = useCallback(
        preventEsc(() => {
            isChanged ? setConfirmIsOpen(true) : onClose();
        }),
        [isChanged],
    );

    const handleConfirmClose = () => {
        setConfirmIsOpen(false);
        onClose();
    };

    const handleAfterSave = useCallback(() => setIsChanged(false), []);

    return (
        <Modal open onOutsideClick={onModalClose}>
            <div className={styles.entitiesSettingsPopup}>
                <SnapshotContext.Provider value={snapshotContext}>
                    <SettingsPageContext.Provider value={settingsPageContext}>
                        <CurrentAliceAppContext.Provider value={currentAliceAppContext}>
                            <GrammarPageEntitiesSettings
                                aliceApp={currentAliceAppContext.aliceApp}
                                onClose={onModalClose}
                                afterSave={handleAfterSave}
                                setIsChanged={setIsChanged}
                                disabled={disabled}
                            />
                        </CurrentAliceAppContext.Provider>
                    </SettingsPageContext.Provider>
                </SnapshotContext.Provider>
            </div>
            {confirmIsOpen && (
                <ModalWithConfirmation
                    title={t('warning-title')}
                    text={t('warning-message')}
                    confirmButtonLabel={tCommon('yes')}
                    cancelButtonLabel={tCommon('no')}
                    onClose={() => setConfirmIsOpen(false)}
                    onConfirm={handleConfirmClose}
                />
            )}
        </Modal>
    );
};

export const GrammarPageEntities: FC<GrammarPageEntitiesProps> = ({ aliceApp, disabled }) => {
    const [popupIsVisible, setPopupIsVisible] = useState(false);
    const { isDraft, isEditable } = useSettingsPageContext();

    const onClick = useCallback(() => setPopupIsVisible(true), []);
    const onClose = useCallback(() => setPopupIsVisible(false), []);

    const code = isDraft ? aliceApp.draft.customEntities : aliceApp.customEntities;

    return (
        <>
            <div className={styles.grammarCodeView}>
                <GranetCodeEditor onChange={(_code) => {}} code={code} readOnly />
            </div>
            <Button onClick={onClick} size="l" disabled={disabled || !isEditable}>
                {t('button-edit')}
            </Button>
            {popupIsVisible && <EntitiesSettingsPopup onClose={onClose} disabled={disabled} />}
        </>
    );
};
